import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '../../components/AppIcon';

// Conversion funnel tracking helper
const trackFunnelEvent = (step, data = {}) => {
  try {
    const events = JSON.parse(sessionStorage.getItem('billing_funnel') || '[]');
    events?.push({ step, ts: Date.now(), ...data });
    sessionStorage.setItem('billing_funnel', JSON.stringify(events));
  } catch (_) {}
};

const Paywall = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    orgName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    numberOfUsers: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  // Controls which step is shown: 'form' | 'interstitial'
  const [step, setStep] = useState('form');

  useEffect(() => {
    trackFunnelEvent('paywall_viewed');
  }, []);

  const validate = () => {
    const errs = {};
    if (!form?.orgName?.trim()) errs.orgName = 'Organization name is required';
    if (!form?.firstName?.trim()) errs.firstName = 'First name is required';
    if (!form?.lastName?.trim()) errs.lastName = 'Last name is required';
    if (!form?.email?.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (form?.numberOfUsers && (isNaN(Number(form?.numberOfUsers)) || Number(form?.numberOfUsers) < 1)) {
      errs.numberOfUsers = 'Must be a positive number';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors?.[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Step 1: validate form and show interstitial
  const handleFormSubmit = (e) => {
    e?.preventDefault();
    setError('');

    const errs = validate();
    if (Object.keys(errs)?.length > 0) {
      setFieldErrors(errs);
      return;
    }

    trackFunnelEvent('interstitial_viewed', { email: form?.email, orgName: form?.orgName });
    setStep('interstitial');
  };

  // Step 2: proceed to Stripe checkout
  const handleCheckout = async () => {
    setError('');
    setIsLoading(true);
    trackFunnelEvent('checkout_initiated', { email: form?.email, orgName: form?.orgName });

    try {
      const customerName = `${form?.firstName} ${form?.lastName}`?.trim();
      const priceId = import.meta.env?.VITE_STRIPE_TRIAL_PRICE_ID;

      if (!priceId || priceId === 'enter-your-value-here' || priceId === 'price_trial_placeholder' || priceId === 'enter-your-stripe-trial-price-id-here') {
        throw new Error(
          'Trial plan is not yet configured. Please set VITE_STRIPE_TRIAL_PRICE_ID in your environment variables to your Stripe Price ID (e.g. price_xxxxx), then redeploy. Contact support at info@servicejockey.net if you need help.'
        );
      }

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Application configuration error. Please contact support.');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller?.abort(), 30000);

      let response;
      try {
        response = await fetch(
          `${supabaseUrl}/functions/v1/create-checkout-session`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              priceId,
              customerEmail: form?.email,
              customerName,
              mode: 'subscription',
              trialDays: 30,
              successUrl: `${window.location?.origin}/login?trial=started`,
              cancelUrl: `${window.location?.origin}/paywall?cancelled=true`,
              metadata: {
                org_name: form?.orgName,
                admin_email: form?.email,
                admin_name: customerName,
                plan: 'starter',
                phone: form?.phone || '',
                number_of_users: form?.numberOfUsers || '1',
                trial_days: '30',
                source: 'paywall_trial_signup',
              },
            }),
            signal: controller?.signal,
          }
        );
      } catch (fetchErr) {
        if (fetchErr?.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        throw fetchErr;
      } finally {
        clearTimeout(timeoutId);
      }

      let data;
      try {
        data = await response?.json();
      } catch (_) {
        throw new Error('Unexpected response from checkout service. Please try again.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.url) {
        trackFunnelEvent('checkout_redirect', { sessionId: data?.sessionId });
        window.location.href = data?.url;
      } else {
        throw new Error('No checkout URL returned. Please try again or contact support.');
      }
    } catch (err) {
      trackFunnelEvent('checkout_error', { error: err?.message });
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    background: 'linear-gradient(180deg, #0D0D0D 0%, #111111 100%)',
    border: '1px solid #3A3A3A',
    color: '#FFFFFF',
    outline: 'none',
  };

  const errorInputStyle = {
    ...inputStyle,
    border: '1px solid #CC0000',
    boxShadow: '0 0 0 2px rgba(204,0,0,0.2)',
  };

  const focusStyle = (e) => {
    e.target.style.borderColor = '#CC0000';
    e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset, 0 0 0 2px rgba(204,0,0,0.25)';
  };
  const blurStyle = (e) => {
    e.target.style.borderColor = '#3A3A3A';
    e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4) inset';
  };

  const FieldError = ({ name }) =>
    fieldErrors?.[name] ? (
      <p className="mt-1 text-xs" style={{ color: '#FF6666' }}>{fieldErrors?.[name]}</p>
    ) : null;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12"
      style={{
        background: 'radial-gradient(ellipse at 30% 20%, #2a0000 0%, #111111 40%, #0a0a0a 70%, #111111 100%)',
      }}
    >
      {/* Top accent stripe */}
      <div
        className="fixed top-0 left-0 right-0 h-1"
        style={{
          background: 'linear-gradient(90deg, #AA0000, #FFD700, #CC0000)',
          boxShadow: '0 2px 12px rgba(255,215,0,0.4)',
        }}
      />
      {/* Ambient layers */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(204,0,0,0.08) 0%, transparent 70%)' }}
      />
      <div className="w-full max-w-lg">
        {/* Brand */}
        <div className="text-center mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mb-3 sm:mb-4 icon-3d"
            style={{
              background: 'linear-gradient(145deg, #E00000 0%, #CC0000 50%, #990000 100%)',
              boxShadow: '0 1px 0 rgba(255,100,100,0.4) inset, 0 -3px 0 rgba(80,0,0,0.6) inset, 0 8px 24px rgba(204,0,0,0.5)',
            }}
          >
            <Icon name="Wrench" size={32} className="sm:hidden" color="white" />
            <Icon name="Wrench" size={38} className="hidden sm:block" color="white" />
          </button>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-widest uppercase"
            style={{ color: '#FFFFFF', letterSpacing: '0.15em', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
          >
            SERVICE<span style={{ color: '#FFD700', textShadow: '0 2px 8px rgba(255,215,0,0.4)' }}>JOCKEY</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, transparent, #CC0000)' }} />
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#FFD700' }}>
              30-Day Free Trial
            </p>
            <div className="h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, #CC0000, transparent)' }} />
          </div>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-5 sm:p-8 card-3d-dark"
          style={{
            background: 'linear-gradient(160deg, #222222 0%, #1C1C1C 40%, #161616 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {/* ── STEP: INTERSTITIAL ── */}
          {step === 'interstitial' ? (
            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <div
                className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full mb-5 sm:mb-6"
                style={{
                  background: 'linear-gradient(145deg, rgba(255,215,0,0.15) 0%, rgba(255,215,0,0.05) 100%)',
                  border: '1px solid rgba(255,215,0,0.3)',
                }}
              >
                <Icon name="Gift" size={26} className="sm:hidden" color="#FFD700" />
                <Icon name="Gift" size={30} className="hidden sm:block" color="#FFD700" />
              </div>

              {/* Heading */}
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4" style={{ color: '#FFFFFF' }}>
                Your 30-Day Free Trial Awaits
              </h2>

              {/* Message */}
              <p className="text-sm sm:text-base leading-relaxed mb-6 sm:mb-8" style={{ color: '#CCCCCC' }}>
                You will receive a 30-day free trial, and to make sure you get the most from the experience, we'll be sure to send you reminders before the trial expires.
              </p>

              {/* Divider */}
              <div className="w-full h-px mb-6 sm:mb-8" style={{ background: 'rgba(255,255,255,0.08)' }} />

              {/* Error (if checkout fails) */}
              {error && (
                <div
                  className="w-full rounded-lg p-3 flex items-start gap-2 mb-4"
                  style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid rgba(204,0,0,0.4)' }}
                >
                  <Icon name="AlertCircle" size={16} color="#FF4444" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-left" style={{ color: '#FF4444' }}>{error}</p>
                </div>
              )}

              {/* Stripe disclaimer */}
              <div
                className="w-full rounded-lg p-3 flex items-start gap-2 mb-4"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <Icon name="Lock" size={15} color="#999999" className="mt-0.5 flex-shrink-0" />
                <p className="text-xs text-left" style={{ color: '#999999' }}>
                  Your payment info is collected securely by Stripe. You will not be charged until your 30-day trial ends. Cancel anytime before then.
                </p>
              </div>

              {/* Continue to Secure Checkout button */}
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 uppercase tracking-wider text-sm btn-3d-red"
                style={{
                  background: isLoading
                    ? 'linear-gradient(180deg, #881111 0%, #771111 100%)'
                    : 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)',
                  color: '#FFFFFF',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.background = 'linear-gradient(180deg, #FF2020 0%, #E00000 50%, #CC0000 100%)';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.background = 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)';
                }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Redirecting to secure checkout...</span>
                  </>
                ) : (
                  <>
                    <Icon name="CreditCard" size={18} color="white" />
                    <span>Continue to Secure Checkout</span>
                  </>
                )}
              </button>

              {/* Back link */}
              <button
                type="button"
                onClick={() => { setStep('form'); setError(''); }}
                className="mt-4 text-sm transition-colors"
                style={{ color: '#666666' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#CCCCCC')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#666666')}
              >
                ← Back to edit information
              </button>
            </div>
          ) : (
            /* ── STEP: FORM ── */
            (<>
              {/* Header */}
              <div className="mb-5 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold mb-1" style={{ color: '#FFFFFF' }}>
                  Start your free 30-day trial
                </h2>
                <p className="text-sm" style={{ color: '#999999' }}>
                  Get your own organization, test with your own data.
                </p>
              </div>
              {/* Trial benefits */}
              <div
                className="rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 flex flex-col gap-2"
                style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.15)' }}
              >
                {[
                  'Your own private organization',
                  'Full access to all features for 30 days',
                  'Use your own real data',
                  'No charge until trial ends — cancel anytime',
                ]?.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={15} color="#FFD700" />
                    <span className="text-xs sm:text-sm" style={{ color: '#CCCCCC' }}>{benefit}</span>
                  </div>
                ))}
              </div>
              {/* Form */}
              <form onSubmit={handleFormSubmit} className="space-y-3 sm:space-y-4" noValidate>
                {error && (
                  <div
                    className="rounded-lg p-3 flex items-start gap-2"
                    style={{ background: 'rgba(204,0,0,0.15)', border: '1px solid rgba(204,0,0,0.4)' }}
                  >
                    <Icon name="AlertCircle" size={16} color="#FF4444" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm" style={{ color: '#FF4444' }}>{error}</p>
                  </div>
                )}

                {/* Organization Name */}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                    Organization / Company Name <span style={{ color: '#FF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="orgName"
                    value={form?.orgName}
                    onChange={handleChange}
                    placeholder="Acme Field Services"
                    className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                    style={fieldErrors?.orgName ? errorInputStyle : inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                  <FieldError name="orgName" />
                </div>

                {/* First / Last Name */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                      First Name <span style={{ color: '#FF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={form?.firstName}
                      onChange={handleChange}
                      placeholder="Jane"
                      className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                      style={fieldErrors?.firstName ? errorInputStyle : inputStyle}
                      onFocus={focusStyle}
                      onBlur={blurStyle}
                    />
                    <FieldError name="firstName" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                      Last Name <span style={{ color: '#FF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={form?.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                      style={fieldErrors?.lastName ? errorInputStyle : inputStyle}
                      onFocus={focusStyle}
                      onBlur={blurStyle}
                    />
                    <FieldError name="lastName" />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                    Work Email <span style={{ color: '#FF4444' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form?.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                    style={fieldErrors?.email ? errorInputStyle : inputStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                  <FieldError name="email" />
                </div>

                {/* Phone + Users */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form?.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                      style={inputStyle}
                      onFocus={focusStyle}
                      onBlur={blurStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#CCCCCC' }}>
                      # of Users
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      name="numberOfUsers"
                      value={form?.numberOfUsers}
                      onChange={handleChange}
                      placeholder="e.g. 5"
                      min="1"
                      className="w-full px-3 sm:px-4 py-2.5 rounded-lg text-sm transition-all"
                      style={fieldErrors?.numberOfUsers ? errorInputStyle : inputStyle}
                      onFocus={focusStyle}
                      onBlur={blurStyle}
                    />
                    <FieldError name="numberOfUsers" />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-xl transition-all duration-200 uppercase tracking-wider text-sm btn-3d-red"
                  style={{
                    background: 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(180deg, #FF2020 0%, #E00000 50%, #CC0000 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(180deg, #E00000 0%, #CC0000 50%, #AA0000 100%)';
                  }}
                >
                  <Icon name="ArrowRight" size={18} color="white" />
                  <span>Continue</span>
                </button>
              </form>
              {/* Back to login */}
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm transition-colors"
                  style={{ color: '#666666' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#CCCCCC')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#666666')}
                >
                  ← Back to login
                </button>
              </div>
            </>)
          )}
        </div>

        <p className="text-center text-xs mt-4" style={{ color: '#555555' }}>
          © {new Date()?.getFullYear()} ServiceJockey. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Paywall;
